import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { CTASection } from '../components/sections/CTASection';
import { 
  ArrowRight, 
  Calendar, 
  Truck, 
  ShieldCheck, 
  Package, 
  Clock,
  CheckCircle2,
  Users,
  MapPin,
  Star,
  Frown,
  Smile,
  Phone,
  Mail
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// How It Works Steps
const steps = [
  {
    number: "01",
    title: "Reserve Online",
    description: "Students reserve storage online in minutes.",
    icon: Calendar
  },
  {
    number: "02", 
    title: "We Pick Up",
    description: "Our team picks up belongings directly from dorms or apartments at the end of the spring semester.",
    icon: Truck
  },
  {
    number: "03",
    title: "Secure Summer Storage",
    description: "Items are stored safely during the summer in secure storage.",
    icon: ShieldCheck
  },
  {
    number: "04",
    title: "Fall Delivery",
    description: "We deliver belongings back when students return for the fall semester.",
    icon: Package
  }
];

// Benefits
const benefits = [
  "Convenient pickup and delivery",
  "Safe and secure storage",
  "Student-focused service",
  "Flexible storage options",
  "Stress-free move-out and move-in",
  "Affordable and practical solution"
];

// Featured Schools
const featuredSchools = [
  "UVA", "Virginia Tech", "Georgetown", "Duke", "GWU"
];

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_student-stash-1/artifacts/u55l23lh_Student%20STORE%20IT%20Logo.png";

export const Home = () => {
  return (
    <div data-testid="home-page" className="bg-white">
      {/* Hero Section - WHITE BACKGROUND */}
      <section 
        className="relative min-h-[85vh] flex items-center bg-white overflow-hidden"
        data-testid="hero-section"
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-10 w-96 h-96 border-2 border-navy rounded-full"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 border-2 border-navy rounded-full"></div>
        </div>

        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <motion.div 
              className="text-left"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/5 border border-navy/10 rounded-full text-navy text-sm font-medium mb-6">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                Trusted by Students Nationwide
              </div>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-navy leading-none mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Student Storage <br />
                <span className="text-red-600">Made Simple</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl leading-relaxed">
                We pick up your belongings at the end of the spring semester, store them safely over the summer, and deliver them back when you return to campus in the fall.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact">
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide text-base px-8 py-4 rounded-lg transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
                    data-testid="hero-reserve-btn"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Reserve Storage Now
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button 
                    variant="outline"
                    className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold uppercase tracking-wide text-base px-8 py-4 rounded-lg transition-all"
                    data-testid="hero-how-it-works-btn"
                  >
                    How It Works
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 text-gray-600 text-sm">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <span>Secure Storage</span>
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-navy" />
                  <span>Free Pickup</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-red-600" />
                  <span>Quick Delivery</span>
                </div>
              </div>
            </motion.div>

            {/* Hero Image - Brand Logo */}
            <motion.div 
              className="relative hidden lg:block"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <img 
                  src={LOGO_URL}
                  alt="Student Store It - Campus Storage - Safe Secure Student-Focused"
                  className="w-full h-auto object-contain"
                />
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-navy">10+ Universities</p>
                    <p className="text-xs text-gray-500">Served This Season</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Timeline - Light Gray Background */}
      <section className="py-16 bg-gray-50 border-y border-gray-100" data-testid="service-timeline">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <motion.div 
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <div className="w-16 h-16 rounded-full bg-navy flex items-center justify-center mb-4 shadow-lg">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Spring Move-Out
              </h3>
              <p className="text-gray-600">We pick up your items at the end of spring semester</p>
            </motion.div>
            
            <motion.div 
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center mb-4 shadow-lg">
                <ShieldCheck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Summer Storage
              </h3>
              <p className="text-gray-600">Your belongings are stored safely all summer</p>
            </motion.div>
            
            <motion.div 
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <div className="w-16 h-16 rounded-full bg-navy flex items-center justify-center mb-4 shadow-lg">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Fall Delivery
              </h3>
              <p className="text-gray-600">We deliver everything back when you return</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before/After Section - WHITE */}
      <section className="section-padding bg-white" data-testid="before-after-section">
        <div className="section-container">
          <motion.div 
            className="text-center mb-12"
            {...fadeUp}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              The Difference We Make
            </span>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Move-Out Stress? We've Got You.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Before Card */}
            <motion.div
              className="relative rounded-2xl overflow-hidden shadow-xl"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-red-500 text-white font-bold text-sm rounded-full flex items-center gap-2">
                <Frown className="w-4 h-4" />
                WITHOUT Student Store It
              </div>
              <img 
                src="https://customer-assets.emergentagent.com/job_0735b5b4-a591-4895-a553-f5ef36c88110/artifacts/93gpswz0_Screenshot_20260310_203954_ChatGPT.jpg"
                alt="Messy dorm room with boxes everywhere during finals"
                className="w-full h-[350px] object-cover"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1748282291775-22810ba98512?q=80&w=800&auto=format&fit=crop";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Stressed & Overwhelmed
                </h3>
                <ul className="text-sm text-gray-200 space-y-1">
                  <li>• Messy dorm room during finals</li>
                  <li>• Boxes and belongings everywhere</li>
                  <li>• Scrambling to find storage</li>
                  <li>• Heavy lifting during exams</li>
                </ul>
              </div>
            </motion.div>

            {/* After Card */}
            <motion.div
              className="relative rounded-2xl overflow-hidden shadow-xl"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-green-500 text-white font-bold text-sm rounded-full flex items-center gap-2">
                <Smile className="w-4 h-4" />
                WITH Student Store It
              </div>
              <img 
                src="/images/2026-02-27-18-55-30-920.jpg"
                alt="Clean organized room after Student Store It pickup"
                className="w-full h-[350px] object-cover"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1557272428-f195cf4ee30c?q=80&w=800&auto=format&fit=crop";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Clean & Organized
                </h3>
                <ul className="text-sm text-gray-200 space-y-1">
                  <li>• Items packed and removed</li>
                  <li>• Clean room for checkout</li>
                  <li>• Focus on finals, not moving</li>
                  <li>• Everything delivered in fall</li>
                </ul>
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="text-center mt-10"
            {...fadeUp}
          >
            <Link to="/contact">
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                data-testid="before-after-cta"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Reserve Your Stress-Free Move-Out
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Real Photos Section - Gray */}
      <section className="section-padding bg-gray-50" data-testid="real-photos-section">
        <div className="section-container">
          <motion.div 
            className="text-center mb-12"
            {...fadeUp}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Our Service In Action
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-navy mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Professional Storage Service
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              className="rounded-xl overflow-hidden shadow-lg bg-white"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/Screenshot_20260224_184808_Gallery.jpg"
                alt="Student Store It team loading truck"
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1715645948484-da40dd56bc93?q=80&w=600&auto=format&fit=crop";
                }}
              />
              <div className="p-4 border-t-4 border-navy">
                <p className="font-bold text-navy">Professional Pickup</p>
              </div>
            </motion.div>
            
            <motion.div
              className="rounded-xl overflow-hidden shadow-lg bg-white"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <img 
                src="/images/2026-02-27-18-55-30-920.jpg"
                alt="Student Store It secure warehouse facility"
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1768796373470-199c0f9521bb?q=80&w=600&auto=format&fit=crop";
                }}
              />
              <div className="p-4 border-t-4 border-red-600">
                <p className="font-bold text-navy">Secure Storage</p>
              </div>
            </motion.div>
            
            <motion.div
              className="rounded-xl overflow-hidden shadow-lg bg-white"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_0735b5b4-a591-4895-a553-f5ef36c88110/artifacts/wr240bdi_Screenshot_20260224_184834_Gallery%20%281%29.jpg"
                alt="Fall delivery service"
                className="w-full h-64 object-cover"
              />
              <div className="p-4 border-t-4 border-navy">
                <p className="font-bold text-navy">Fall Delivery</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section - WHITE */}
      <section className="section-padding bg-white" data-testid="mission-section">
        <div className="section-container">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            {...fadeUp}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Our Mission
            </span>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Making Student Life Easier
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Student Store It exists to make moving and storing belongings easier for college students. 
              Our mission is to provide a safe, secure, convenient, and student-focused storage solution 
              during school breaks and transitions.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              We handle the pickup, storage, and delivery so students can focus on exams, travel, 
              internships, and returning to campus stress-free.
            </p>
            
            {/* Tagline */}
            <div className="flex items-center justify-center gap-4 text-navy font-bold text-lg">
              <span>Safe</span>
              <span className="w-2 h-2 rounded-full bg-red-600"></span>
              <span>Secure</span>
              <span className="w-2 h-2 rounded-full bg-red-600"></span>
              <span>Student-Focused</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section - Gray */}
      <section className="section-padding bg-gray-50" data-testid="how-it-works-section">
        <div className="section-container">
          <motion.div 
            className="text-center mb-16"
            {...fadeUp}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Simple Process
            </span>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              How It Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className="relative bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`step-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-xl bg-navy/10 flex items-center justify-center mb-4">
                  <step.icon className="w-7 h-7 text-navy" />
                </div>
                <span className="text-xs font-mono font-bold text-red-600 mb-2 block">
                  STEP {step.number}
                </span>
                <h3 
                  className="text-xl font-bold text-navy mb-2"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            {...fadeUp}
          >
            <Link to="/how-it-works">
              <Button 
                className="bg-navy hover:bg-navy/90 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-lg shadow-lg"
                data-testid="learn-more-btn"
              >
                Learn More About Our Process
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section - WHITE */}
      <section className="section-padding bg-white" data-testid="benefits-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
                Why Students Choose Us
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-navy mb-8"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                The Smart Choice for <br />Student Storage
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    {/* RED CHECKMARKS */}
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/contact">
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Reserve Now
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <img 
                  src={LOGO_URL}
                  alt="Student Store It Logo"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-navy text-white rounded-xl p-6 shadow-xl max-w-xs">
                <Users className="w-8 h-8 text-red-400 mb-2" />
                <p className="font-bold text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Built for Students
                </p>
                <p className="text-gray-300 text-sm">
                  Powered by campus teams who understand student life
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schools We Serve Preview - Gray */}
      <section className="section-padding bg-gray-50" data-testid="schools-preview-section">
        <div className="section-container">
          <motion.div 
            className="text-center mb-12"
            {...fadeUp}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Campus Network
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-navy mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Schools We Serve
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Student Store It currently serves students at top universities across the country.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {featuredSchools.map((school, index) => (
              <motion.div
                key={school}
                className="px-6 py-3 bg-white rounded-full shadow-sm border border-gray-200 text-navy font-medium"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <MapPin className="w-4 h-4 inline-block mr-2 text-red-600" />
                {school}
              </motion.div>
            ))}
            <Link to="/schools">
              <motion.div
                className="px-6 py-3 bg-navy text-white rounded-full shadow-sm font-medium cursor-pointer hover:bg-navy/90 transition-colors"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.5 }}
                data-testid="view-all-schools-btn"
              >
                +5 More Schools →
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Info Section - WHITE */}
      <section className="py-12 bg-white border-t border-gray-100" data-testid="contact-info-section">
        <div className="section-container">
          <div className="flex flex-wrap justify-center gap-12 text-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Customer Support</p>
                <a href="tel:+15407654298" className="text-navy font-bold text-lg hover:text-red-600 transition-colors">
                  +1 (540) 765-4298
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Email Us</p>
                <a href="mailto:mandy@studentstoreit.com" className="text-navy font-bold text-lg hover:text-red-600 transition-colors">
                  mandy@studentstoreit.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Operating Hours</p>
                <p className="text-navy font-bold">Mon - Sat: 9AM - 5PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Navy Background */}
      <CTASection 
        headline="Reserve Your Summer Storage Today"
        subtext="$30 deposit secures your spot. Book now before finals week!"
        primaryBtnText="Reserve Storage Now"
        secondaryBtnText="View Pricing"
        secondaryLink="/pricing"
      />
    </div>
  );
};

export default Home;
